import React, { useContext, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import API from '../../../../services/API'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import { Alert, Button, Chip, Grid, Paper, Typography } from '@mui/material'
import { DetailRow } from '../../../../components/Cards/DetailsBox'
import moment from 'moment'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'
import { Add } from '@mui/icons-material'
import AddNewControlRoomPopup from './AddCustomControlRoom'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import AllControlRooms from './AllControlRooms'

import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import PersonIcon from '@mui/icons-material/Person'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import LoginIcon from '@mui/icons-material/Login'
import { useNavigate } from 'react-router-dom'

const ControlRoomObject = ({ room }) => {
  const { setSiteMode, setSelectedControlRoom } = useContext(SiteModeContext)
  const navigate = useNavigate()

  const title = room?.customTitle ?? room?.linkedEvent?.title ?? 'Unknown'

  return (
    <Grid item xs={12} md={6} lg={4}>
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid #E2E8F0',
          boxShadow: '0 12px 34px rgba(36,47,64,0.08)',
          transition: 'all 0.22s ease',
          position: 'relative',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 18px 46px rgba(36,47,64,0.14)',
            borderColor: '#239AA4',
          },
          '&:before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at top right, rgba(35,154,164,0.16), transparent 34%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Stack spacing={2} sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #242F40 0%, #239AA4 130%)',
                color: 'white',
                boxShadow: '0 12px 26px rgba(35,154,164,0.26)',
                flexShrink: 0,
              }}
            >
              <MeetingRoomIcon />
            </Box>

            <Stack spacing={0.75} flex={1}>
              <Typography fontWeight={900} color="#242F40" lineHeight={1.2}>
                {title}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={room.status}
                  size="small"
                  sx={{
                    borderRadius: 999,
                    backgroundColor:
                      room.status === 'Active' ? '#DCFCE7' : '#F1F5F9',
                    color: room.status === 'Active' ? '#166534' : '#242F40',
                    fontWeight: 800,
                  }}
                />

                <Chip
                  label={`Room ${room.controlRoomRef}`}
                  size="small"
                  sx={{
                    borderRadius: 999,
                    backgroundColor: '#E6F7F9',
                    color: '#1E7F88',
                    fontWeight: 800,
                  }}
                />
              </Stack>
            </Stack>
          </Stack>

          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarMonthIcon sx={{ fontSize: 18, color: '#64748B' }} />
              <Typography variant="body2" color="text.secondary">
                Started {moment(room.started).format('Do MMM YYYY')}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <PersonIcon sx={{ fontSize: 18, color: '#64748B' }} />
              <Typography variant="body2" color="text.secondary">
                {room.owner?.firstName} {room.owner?.lastName}
              </Typography>
            </Stack>
          </Stack>

          <Button
            onClick={() => {
              // setSelectedControlRoom(room._id)
              // setSiteMode('eventControl')
              navigate(`/commandcontrol/controlroom/${room._id}`)
            }}
            variant="contained"
            endIcon={<LoginIcon />}
            fullWidth
            sx={{
              borderRadius: 999,
              backgroundColor: '#239AA4',
              textTransform: 'none',
              fontWeight: 800,
              '&:hover': {
                backgroundColor: '#1E7F88',
              },
            }}
          >
            Enter Control Room
          </Button>
        </Stack>
      </Paper>
    </Grid>
  )
}

const ActiveControlRooms = () => {
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [rooms, setRooms] = useState([])

  useEffect(() => {
    API.get('/controlroominfo/dashboard/activecontrolrooms')
      .then((res) => {
        if (res.status === 200) {
          setRooms(res.data)
        }
      })
      .catch((e) => {
        console.log(e)
        setFetchError(
          e?.message ?? 'Unable to load Control Rooms. Please try again later',
        )
      })
      .finally(() => setLoading(false))
  }, [])

  return (
    <Stack mt={2} gap={1}>
      {loading && <LinearProgress />}
      {fetchError && <Alert severity="error">{fetchError}</Alert>}
      {!loading && rooms.length === 0 && (
        <Alert severity="info">There are no Active Control Rooms</Alert>
      )}
      <Stack justifyContent="flex-end" gap={1}>
        <Grid container spacing={2}>
          {rooms.map((x) => (
            <ControlRoomObject key={x._id} room={x} />
          ))}
        </Grid>
      </Stack>
      <AllControlRooms />
    </Stack>
  )
}

export default ActiveControlRooms
